import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';
import { LoginForm } from '../sections/auth/login';
import headerImgWebp from '../assets/headerImg.webp';
import headerImg from '../assets/headerImg.png';
import brandLogo from '../assets/finalLogo.png';

/**
 * Admin login — restructured to mirror the portal login (CR-P1 Phase 2).
 *
 * Same split-panel layout as English-Teacher-Website's AuthSplitLayout:
 * a brand column on the left with the ET logo, welcome copy, rainbow
 * accent bar, and the girl-in-glasses hero image; the LoginForm on the
 * right. Purple/pink brand tokens and Inter come from theme.js.
 *
 * Kept simple: no shared "AuthSplitLayout" component in admin because
 * the admin only has one auth page. The visual result matches the
 * portal so the two apps feel like one product.
 */

const RAINBOW_STOPS = ['#e76f8e', '#f0a04b', '#f4d35e', '#83c46b', '#7fb1e5', '#b07cc6'];

export default function LoginPage() {
    return (
        <>
            <Helmet>
                <title> Login | English Teacher </title>
            </Helmet>
            <Box
                sx={{
                    // Lock wrapper to viewport exactly so the login never
                    // introduces a page-level scroll. The right-hand form
                    // column scrolls internally on very short viewports.
                    height: '100vh',
                    width: '100vw',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#f5f7fb',
                    p: { xs: 0, md: 3, lg: 4 },
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        height: '100%',
                        maxWidth: 1180,
                        bgcolor: '#fff',
                        borderRadius: { xs: 0, md: 3 },
                        boxShadow: {
                            xs: 'none',
                            md: '0 30px 80px -30px rgba(20,30,60,0.18)',
                        },
                        overflow: 'hidden',
                    }}
                >
                    {/* LEFT — brand panel */}
                    <Box
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            flex: '0 0 50%',
                            flexDirection: 'column',
                            bgcolor: '#fff',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ position: 'relative', zIndex: 1, p: { md: 6, lg: 7 }, pb: 0 }}>
                            <Typography
                                sx={{
                                    display: 'block',
                                    color: '#444',
                                    fontSize: 12,
                                    mb: 0.5,
                                    fontStyle: 'italic',
                                }}
                            >
                                Specialist English Tuition Centre
                            </Typography>
                            <Box
                                component="img"
                                src={brandLogo}
                                alt="English Teacher"
                                sx={{ width: 200, mb: 0.5, display: 'block' }}
                            />
                            <Typography sx={{ display: 'block', color: '#444', fontSize: 11, letterSpacing: 1 }}>
                                PRIMARY · GCSE · A-LEVEL
                            </Typography>
                            <Typography sx={{ display: 'block', color: '#444', fontSize: 11, fontStyle: 'italic' }}>
                                www.englishteacher.co.uk
                            </Typography>
                        </Box>

                        <Box sx={{ position: 'relative', zIndex: 1, px: { md: 6, lg: 7 }, mt: { md: 5, lg: 7 } }}>
                            <Typography
                                sx={{
                                    fontSize: { md: 40, lg: 48 },
                                    lineHeight: 1.1,
                                    fontWeight: 700,
                                    color: '#1a1a1a',
                                    maxWidth: 420,
                                }}
                            >
                                Admin Console
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    mt: 2,
                                    mb: 2,
                                    width: 200,
                                    height: 4,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                }}
                            >
                                {RAINBOW_STOPS.map((c) => (
                                    <Box key={c} sx={{ flex: 1, bgcolor: c }} />
                                ))}
                            </Box>
                            <Typography sx={{ color: '#5b5b5b', fontSize: 15, maxWidth: 320 }}>
                                Manage students, classes, reports, invoices and messages —
                                all in one place.
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                position: 'relative',
                                zIndex: 1,
                                mt: 'auto',
                                width: '100%',
                                height: { md: 340, lg: 380 },
                                overflow: 'hidden',
                            }}
                        >
                            <picture>
                                <source srcSet={headerImgWebp} type="image/webp" />
                                <Box
                                    component="img"
                                    src={headerImg}
                                    alt=""
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        objectPosition: 'center center',
                                        display: 'block',
                                    }}
                                />
                            </picture>
                            <Box
                                aria-hidden
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    pointerEvents: 'none',
                                    background: `
                                        linear-gradient(to bottom, #fff 0%, rgba(255,255,255,0) 28%),
                                        linear-gradient(to right, #fff 0%, rgba(255,255,255,0) 6%),
                                        linear-gradient(to left, #fff 0%, rgba(255,255,255,0) 6%)
                                    `,
                                }}
                            />
                        </Box>
                    </Box>

                    {/* RIGHT — form column.
                        overflow-y: auto so short viewports can still fill
                        the form without the whole page scrolling. */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: { xs: 3, sm: 6, md: 6, lg: 10 },
                            py: { xs: 6, md: 4 },
                            bgcolor: '#fff',
                            overflowY: 'auto',
                        }}
                    >
                        <Container maxWidth="sm" sx={{ px: '0 !important' }}>
                            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                                Sign in
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary' }}>
                                Admin access to English Teacher.{' '}
                                <Link to="/register" style={{ color: '#6D3FC5', fontWeight: 600 }}>
                                    Sign up
                                </Link>
                            </Typography>
                            <LoginForm />
                        </Container>
                    </Box>
                </Box>
            </Box>
        </>
    );
}
